import { Fragment, memo, type FC } from "react";
import {
  MAX_SEGMENT_CONDITIONS,
  type ConditionCategory,
  type ConditionOperator,
  type SegmentCondition,
  type SegmentRules,
} from "../CreateSegment.Constants";
import { isConditionComplete } from "../CreateSegment.Utils";
import { useSegmentFormatters, useSegmentLabel } from "../UseSegmentLabel";
import SegmentConditionRow from "./SegmentConditionRow";

interface SegmentConditionsCardProps {
  rules: SegmentRules;
  /** Matching customers per condition from the live preview, in condition order. */
  stepCounts?: number[];
  showErrors: boolean;
  onChange: (_condition: SegmentCondition) => void;
  onChangeCategory: (_id: number, _category: ConditionCategory) => void;
  onAdd: (_operator: ConditionOperator) => void;
  onDuplicate: (_id: number) => void;
  onRemove: (_id: number) => void;
  onToggleOperator: (_index: number) => void;
}

const SegmentConditionsCard: FC<SegmentConditionsCardProps> = ({
  rules,
  stepCounts,
  showErrors,
  onChange,
  onChangeCategory,
  onAdd,
  onDuplicate,
  onRemove,
  onToggleOperator,
}) => {
  const label = useSegmentLabel();
  const { formatValue } = useSegmentFormatters();
  const { conditions, operators } = rules;
  const usedCount = conditions.length;
  const isAtLimit = usedCount >= MAX_SEGMENT_CONDITIONS;
  const canAdd = !isAtLimit && isConditionComplete(conditions[usedCount - 1]);
  const isSingleEmpty = usedCount === 1 && conditions[0].category === null;

  const addHint =
    canAdd || isAtLimit
      ? label("conditions.used", { used: formatValue(usedCount), max: formatValue(MAX_SEGMENT_CONDITIONS) })
      : label(usedCount === 1 ? "conditions.finishFirst" : "conditions.finishLast");

  return (
    <section className="campaign-flow__card segment-builder__card">
      <div className="segment-builder__card-content">
        <header className="segment-builder__card-header">
          <h2 className="segment-builder__card-title">{label("conditions.title")}</h2>
          <p className="segment-builder__card-subtitle">
            {label("conditions.subtitle", { max: formatValue(MAX_SEGMENT_CONDITIONS) })}
          </p>
        </header>

        {conditions.map((condition, index) => (
          <Fragment key={condition.id}>
            {index > 0 && (
              <div className="segment-builder__connector">
                <button
                  type="button"
                  className="segment-builder__connector-pill"
                  aria-label={label("operators.toggleAria")}
                  onClick={() => onToggleOperator(index - 1)}
                >
                  {label(`operators.${operators[index - 1]}`)}
                </button>
                <span className="segment-builder__connector-text">
                  {label(`operators.${operators[index - 1]}Hint`)}
                </span>
              </div>
            )}
            <SegmentConditionRow
              condition={condition}
              position={index + 1}
              peopleCount={
                isConditionComplete(condition) ? stepCounts?.[index] ?? null : null
              }
              showError={showErrors}
              canDuplicate={!isAtLimit}
              canRemove={!isSingleEmpty}
              onChange={onChange}
              onChangeCategory={onChangeCategory}
              onDuplicate={onDuplicate}
              onRemove={onRemove}
            />
          </Fragment>
        ))}

        <div className="segment-builder__add-row">
          <button
            type="button"
            className="segment-builder__add-btn"
            disabled={!canAdd}
            onClick={() => onAdd("and")}
          >
            {label("conditions.addAnd")}
          </button>
          <button
            type="button"
            className="segment-builder__add-btn"
            disabled={!canAdd}
            onClick={() => onAdd("or")}
          >
            {label("conditions.addOr")}
          </button>
          <span className="segment-builder__add-hint">{addHint}</span>
        </div>
      </div>
    </section>
  );
};

export default memo(SegmentConditionsCard);
